import { isReadOnlyAdmin } from "@/lib/auth";
import { createFileRoute, getRouteApi } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { BillingUpgradeBanner } from "@/components/billing-upgrade-banner";
import { FXDisabledBanner } from "@/components/fx-disabled-banner";
import { getMessageFromError, ProviderIssueError } from "@/lib/api-errors";
import {
  applicationApi,
  ApplicationConfigNotFoundError,
} from "@/lib/application-api";
import { fxQueryKeys } from "@/lib/fx-query-keys";
import { payinAccountsWithAggregatedBalancesQuery } from "@/lib/queries";
import { queryRetry } from "@/lib/query-retry";
import {
  STABLECOIN_CURRENCIES,
  useStablecoinRatesEnabled,
} from "@/lib/stablecoin-rates";
import { ExchangeWalletSection } from "../-components/exchange-wallet-section/exchange-wallet-section";
import { useIsFXEnabled } from "../-components/is-exchange-enabled";
import { RatesPanel } from "./-components/landing/rates-panel";
import {
  buildDefaultPayInAccountIdByCurrency,
  buildDefaultVaNameByCurrency,
  buildSourceWalletOptions,
  buildVaBalanceByCurrency,
  FX_DISABLED_DESTINATION_CURRENCIES,
  type DestinationAvailability,
} from "./-components/landing/trade-panel-state";
import { TradePanel } from "./-components/landing/trade-panel";
import {
  resolveBulkRatesAndRatesPanelAxes,
  useExchangeRatesFeed,
} from "./-components/landing/use-exchange-rates-feed";

export const Route = createFileRoute("/(app)/_main-layout/_exchange/exchange/")(
  {
    component: ExchangePageComponent,
    loader: ({ context }) => {
      // Pay-in balances are independent of bulk rates and the rest of the page;
      // prefetch without blocking route transition.
      void context.queryClient.prefetchQuery(
        payinAccountsWithAggregatedBalancesQuery(
          context.merchantId,
          context.accessToken,
          context.selectedApplicationId,
        ),
      );
    },
  },
);

const exchangePageRoute = getRouteApi(
  "/(app)/_main-layout/_exchange/exchange/",
);

const EMPTY_DESTINATION_AVAILABILITY: readonly DestinationAvailability[] = [];

function ExchangePageComponent() {
  const isFxEnabled = useIsFXEnabled();
  return <ExchangePageContent isFxEnabled={isFxEnabled} />;
}

function ExchangePageContent({
  isFxEnabled,
}: {
  readonly isFxEnabled: boolean | undefined;
}) {
  const context = exchangePageRoute.useRouteContext();

  const eligibilityQuery = useQuery({
    queryKey: fxQueryKeys.eligibility(context.selectedApplicationId),
    queryFn: () =>
      applicationApi.getFxEligibility(
        context.selectedApplicationId,
        context.accessToken,
      ),
    enabled: isFxEnabled === true,
    retry: queryRetry(
      (_, error) =>
        !(
          error instanceof ApplicationConfigNotFoundError ||
          error instanceof ProviderIssueError
        ),
    ),
  });
  const payinBalancesQuery = useQuery(
    payinAccountsWithAggregatedBalancesQuery(
      context.merchantId,
      context.accessToken,
      context.selectedApplicationId,
    ),
  );

  const payinBalanceEntries = payinBalancesQuery.data ?? [];
  const vaBalanceByCurrency = buildVaBalanceByCurrency(payinBalanceEntries);
  const defaultPayInAccountIdByCurrency =
    buildDefaultPayInAccountIdByCurrency(payinBalanceEntries);
  const defaultVaNameByCurrency =
    buildDefaultVaNameByCurrency(payinBalanceEntries);

  const destinationAvailability =
    eligibilityQuery.data?.destinationAvailability ??
    EMPTY_DESTINATION_AVAILABILITY;
  const destinationBeneficiaries =
    eligibilityQuery.data?.destinationBeneficiaries ?? [];
  const sourceWalletOptions = buildSourceWalletOptions({
    isFxEnabled,
    sourceWalletOptions: eligibilityQuery.data?.sourceWalletOptions,
    gbpVaBalance: vaBalanceByCurrency.get("GBP") ?? 0,
  });
  const tradeDestinationCurrencies = isFxEnabled
    ? (eligibilityQuery.data?.destinationCurrencies ?? [])
    : [...FX_DISABLED_DESTINATION_CURRENCIES];

  const stablecoinRatesEnabled = useStablecoinRatesEnabled();
  const {
    bulkRatePairs,
    ratesPanelSourceCurrencies,
    ratesPanelDestinationCurrencies,
  } = useMemo(
    () =>
      resolveBulkRatesAndRatesPanelAxes(
        isFxEnabled,
        eligibilityQuery.data,
        stablecoinRatesEnabled,
      ),
    [isFxEnabled, eligibilityQuery.data, stablecoinRatesEnabled],
  );

  const { exchangeRates, ratesLastUpdated, isRatesError } =
    useExchangeRatesFeed(bulkRatePairs);

  const showBanners = isFxEnabled !== undefined;
  const tradePanel = isReadOnlyAdmin(context.authorities) ? null : (
    <TradePanel
      isFxEnabled={isFxEnabled}
      isPayinBalancesLoading={payinBalancesQuery.isLoading}
      isEligibilityLoading={eligibilityQuery.isLoading}
      isEligibilityError={eligibilityQuery.isError}
      eligibilityErrorMessage={getMessageFromError(eligibilityQuery.error)}
      sourceWalletOptions={sourceWalletOptions}
      destinationCurrencies={tradeDestinationCurrencies}
      destinationAvailability={destinationAvailability}
      destinationBeneficiaries={destinationBeneficiaries}
      defaultVaNameByCurrency={defaultVaNameByCurrency}
      defaultPayInAccountIdByCurrency={defaultPayInAccountIdByCurrency}
      vaBalanceByCurrency={vaBalanceByCurrency}
      exchangeRates={exchangeRates}
      comingSoonDestinationCurrencies={
        stablecoinRatesEnabled ? [...STABLECOIN_CURRENCIES] : []
      }
    />
  );
  const ratesPanel = (
    <RatesPanel
      sourceCurrencies={ratesPanelSourceCurrencies}
      destinationCurrencies={ratesPanelDestinationCurrencies}
      exchangeRates={exchangeRates}
      ratesLastUpdated={ratesLastUpdated}
      isRatesError={isRatesError}
    />
  );

  return (
    <>
      {showBanners && (
        <>
          <FXDisabledBanner />
          <BillingUpgradeBanner />
        </>
      )}

      {isFxEnabled === false ? ratesPanel : tradePanel}

      {isFxEnabled === true && <ExchangeWalletSection />}

      <div className="mt-6">
        {isFxEnabled === false ? tradePanel : ratesPanel}
      </div>
    </>
  );
}
